/**
 * @file RedirectTargetField.tsx
 * @description Поле «Куда» переадресации (phase19.16, партия Р3в, v0.19.64;
 * SB131, SB128; uiux-r3 §3.5, db-design-r3v §7): вид цели — страница, товар,
 * категория, статья, рубрика или адрес на этом сайте; сущность выбирается
 * поиском по списку магазина (`usePickList`, как в элементах секции и меню),
 * адрес — строкой с подсказкой по таблице маршрутов shared.
 *
 * Общее для окна «Добавить переадресацию» и окна удаления страницы (SB128):
 * набор видов задаёт вызывающий (`kinds`). Цель — сущность, а не адрес:
 * следующее переименование правило не ломает.
 */

import { useId } from "react";

import { Combobox } from "../../ui/combobox";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { SelectField } from "../../ui/SelectField";
import { usePickList } from "../../../hooks/site-builder/usePickList";
import type { RedirectTargetValue } from "../../../lib/site-seo/redirect-target";
import { REDIRECT_TARGET_KINDS, REDIRECT_TARGET_ORDER, type RedirectTargetKind } from "../../../lib/site-seo/redirect-view";

interface Props {
  value: RedirectTargetValue;
  onChange: (value: RedirectTargetValue) => void;
  error?: string | null;
  disabled?: boolean;
  /** Какие виды предлагать (по умолчанию все). */
  kinds?: readonly RedirectTargetKind[];
  /** Чего не предлагать в списке (удаляемая страница). */
  excludeIds?: readonly string[];
}

export function RedirectTargetField({ value, onChange, error = null, disabled = false, kinds = REDIRECT_TARGET_ORDER, excludeIds = [] }: Props) {
  const baseId = useId();
  const meta = REDIRECT_TARGET_KINDS[value.kind];
  const { options, failed } = usePickList(meta.list ?? null);
  const choices = (options ?? []).filter((o) => !excludeIds.includes(o.id));

  return (
    <div className="flex min-w-0 flex-col gap-2">
      <div className="flex min-w-0 flex-col gap-1">
        <Label htmlFor={`${baseId}-kind`}>Куда</Label>
        <SelectField
          id={`${baseId}-kind`}
          ariaLabel="Куда вести"
          value={value.kind}
          disabled={disabled}
          options={kinds.map((kind) => ({ value: kind, label: REDIRECT_TARGET_KINDS[kind].label }))}
          onChange={(kind) => onChange({ kind: kind as RedirectTargetKind, id: null, path: "" })}
        />
      </div>
      {meta.list ? (
        failed ? (
          <p className="type-caption text-on-surface-variant">Не удалось загрузить список. Выберите вид «Адрес» и укажите адрес на этом сайте, например /about.</p>
        ) : (
          <Combobox
            id={`${baseId}-target`}
            ariaLabel={meta.label}
            options={choices.map((o) => ({ value: o.id, label: o.label, description: o.description || undefined }))}
            value={value.id}
            disabled={disabled}
            invalid={!!error}
            placeholder={options ? "Выберите…" : "Загружаем…"}
            searchPlaceholder="Найти по названию"
            onChange={(id) => onChange({ ...value, id })}
          />
        )
      ) : (
        <Input
          id={`${baseId}-target`}
          aria-label="Адрес на этом сайте"
          inputMode="url"
          placeholder="/catalog"
          value={value.path}
          disabled={disabled}
          aria-invalid={!!error}
          onChange={(e) => onChange({ ...value, path: e.target.value })}
        />
      )}
      {error ? <p className="type-caption text-destructive">{error}</p> : null}
    </div>
  );
}
